import axios, {parseResponseData} from '@/axiosInstance.ts'
import {MedicalRecordAggregatedPrint} from '@/features/medical-records/types'
import {PaginationParams, ResponseType} from '@/types'
import {captureException} from '@sentry/react'
import {z} from 'zod'

// Create medical record
export type HttpCreateMedicalRecordOptions = {
    payload: {patientId: string}
}
export const httpCreateMedicalRecord = ({payload}: HttpCreateMedicalRecordOptions) =>
    axios.post(`medical-records`, payload)

// Get medical record
export interface HttpGetPatientMedicalRecordOptions {
    params: PaginationParams & {
        patientId: string
        search?: string
        responseType?: ResponseType
    }
}
export const httpGetPatientMedicalRecord = ({params}: HttpGetPatientMedicalRecordOptions) =>
    axios.get(`medical-record-reports/index-grouped-by-projects`, {params})

// Get medical record aggregated prints
export const HttpGetMedicalRecordAggregatedPrintPayload = z.object({
    patientId: z.string().min(1),
    userId: z.string().min(1),
    date: z.string().min(1),
    medicalRecordReportTypeId: z.string().min(1)
})
export type HttpGetMedicalRecordAggregatedPrintPayload = z.infer<typeof HttpGetMedicalRecordAggregatedPrintPayload>

export const httpGetMedicalRecordAggregatedPrint = async (payload: HttpGetMedicalRecordAggregatedPrintPayload) => {
    const response = await axios.post(`/aggregated-prints`, payload)
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: MedicalRecordAggregatedPrint
    })
}

// Generate medical record cover
export type HttpGenerateMedicalRecordCoverOptions = {
    urlParams: {medicalRecordId?: string}
}
export const httpGenerateMedicalRecordCover = ({urlParams}: HttpGenerateMedicalRecordCoverOptions) =>
    axios.post(`medical-records/${urlParams.medicalRecordId}/generate-cover`)

export type HttpGetMedicalRecordCoverOptions = {
    urlParams: {medicalRecordId?: string}
    params: {
        responseType?: ResponseType
    }
}
export const HttpGetMedicalRecordCoverResponse = z.object({
    id: z.string().min(1),
    code: z.number(),
    coverAttachment: z
        .object({
            url: z.string().url()
        })
        .nullable(),
    pdfGenerationStartedAt: z.string().nullable(),
    pdfGenerationCompletedAt: z.string().nullable()
})

export const httpGetMedicalRecordCover = async ({urlParams, params}: HttpGetMedicalRecordCoverOptions) => {
    const response = await axios.get(`medical-records/${urlParams.medicalRecordId}`, {params})
    return parseResponseData({
        responseData: response.data,
        onZodError: captureException,
        responseShape: HttpGetMedicalRecordCoverResponse
    })
}
